import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Chip,
    useTheme,
    useMediaQuery,
    ButtonGroup,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    CircularProgress,
    Tabs,
    Tab,
    Autocomplete,
    Menu,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Person as PersonIcon,
    CalendarMonth as CalendarIcon,
    Event as EventIcon,
    AccountCircle as AccountCircleIcon,
    DragIndicator as DragIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    TableChart as TableIcon,
    ViewModule as KanbanIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Polyline as PolylineIcon,
    ManageAccounts as ManageAccountsIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Delete as DeleteIcon,
    Warning as WarningIcon,
    Sync as SyncIcon,
    ElectricalServices as TowerIcon,
    AccountTree as AccountTreeIcon,
    ExpandMore as ExpandMoreIcon,
    Archive as ArchiveIcon,
    Clear as ClearIcon,
    LocationOn as LocationIcon
} from '@mui/icons-material';
import { getArchivedTasks } from '../../../http/userApi';

export const DeletedTasksDialog = ({
    users,
    handleDialogClose,
    darkMode,
    isMobile,
    statusDefs,
    formatDate
}) => {
    const [tasks, setTasks] = useState([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        loadArchivedTasks()
    }, [])

    const loadArchivedTasks = async () => {
        setLoading(true);
        try {
            const response = await getArchivedTasks();
            setTasks(response.data || []);
        } catch (error) {
            console.error('Ошибка загрузки архивных задач:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
                open={true}
                onClose={handleDialogClose}
                aria-labelledby="archive-dialog-title"
                aria-describedby="archive-dialog-description"
                maxWidth="xl"
                fullWidth
                sx={{
                    zIndex: 6500,
                    '& .MuiDialog-paper': {
                        width: '95vw',
                        maxWidth: '1400px'
                    }
                }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                            color: darkMode ? '#f5f7fa' : '#333',
                            maxHeight: '80vh'
                        }
                    }
                }}
            >
                <DialogTitle id="archive-dialog-title" sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    color: darkMode ? '#f5f7fa' : '#333',
                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            border: '2px solid #f44336'
                        }}>
                            <ArchiveIcon sx={{
                                fontSize: '24px',
                                color: '#f44336'
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                color: darkMode ? '#f5f7fa' : '#333'
                            }}>
                                Удаленные задачи
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.875rem'
                            }}>
                                Задачи, которые были удалены
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton
                        onClick={handleDialogClose}
                        size="large"
                        sx={{
                            color: darkMode ? '#f5f7fa' : '#666',
                            '&:hover': {
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                color: '#f44336'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent id="archive-dialog-description" sx={{
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    p: 0
                }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            py: 4
                        }}>
                            <CircularProgress sx={{
                                color: darkMode ? '#f44336' : '#d32f2f'
                            }} />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} sx={{
                            bgcolor: 'transparent',
                            boxShadow: 'none',
                            maxHeight: '60vh',
                            '& .MuiTable-root': {
                                minWidth: '1200px' // Минимальная ширина для всех колонок
                            }
                        }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '60px' }}>
                                                ID
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '150px' }}>
                                                Название
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '200px' }}>
                                                Описание
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '100px' }}>
                                                Статус
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                                                Создана
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                                                Создатель
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                                                Ответственный
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                                                Дедлайн
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                                                Удалена
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            color: darkMode ? '#f5f7fa' : '#333',
                                            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`
                                        }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: '120px' }}>
                                                Кто удалил
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tasks.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={10}
                                                align="center"
                                                sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#bbb' : '#666',
                                                    py: 4,
                                                    borderBottom: 'none'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                    <ArchiveIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                                                    <Typography variant="body1">
                                                        Удаленных задач не найдено
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        tasks.map((task) => (
                                            <TableRow key={task.id} sx={{
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
                                                }
                                            }}>
                                                {/* ID */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {task.id}
                                                    </Typography>
                                                </TableCell>

                                                {/* Название */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {task.title || task.name || 'Без названия'}
                                                    </Typography>
                                                </TableCell>

                                                {/* Описание */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    {task.description ? (
                                                        <Typography variant="caption" sx={{
                                                            color: darkMode ? '#bbb' : '#666',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            lineHeight: 1.3
                                                        }}>
                                                            {task.description.replace(/<[^>]*>/g, '')}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: darkMode ? '#777' : '#999', fontStyle: 'italic' }}>
                                                            Без описания
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Статус */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    {task.status ? (
                                                        <Chip
                                                            label={statusDefs.find(s => s.id === task.status)?.title || task.status}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: statusDefs.find(s => s.id === task.status)?.color || '#666',
                                                                color: 'white',
                                                                fontSize: '0.7rem'
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: darkMode ? '#777' : '#999' }}>
                                                            Не указан
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Дата создания */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="caption">
                                                        {formatDate(task.created_at)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Создатель */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="caption">
                                                        {users.get(task.creator) || ''}
                                                    </Typography>
                                                </TableCell>

                                                {/* Ответственный */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="caption">
                                                        {users.get(task.responsible) || ''}
                                                    </Typography>
                                                </TableCell>

                                                {/* Дедлайн */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="caption">
                                                        {formatDate(task.deadline)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Дата архивирования */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="caption">
                                                        {formatDate(task.edited_at)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Кто архивировал */}
                                                <TableCell sx={{
                                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
                                                }}>
                                                    <Typography variant="caption">
                                                        {users.get(task.editor) || ''}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    borderTop: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    py: 2,
                    px: 3
                }}>
                    <Button
                        onClick={handleDialogClose}
                        variant="outlined"
                        sx={{
                            color: darkMode ? '#bbb' : '#666',
                            borderColor: darkMode ? '#555' : '#ddd',
                            '&:hover': {
                                borderColor: darkMode ? '#777' : '#bbb',
                                bgcolor: 'transparent'
                            }
                        }}
                    >
                        Закрыть
                    </Button>
                    <Button
                        onClick={loadArchivedTasks}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            bgcolor: darkMode ? '#f44336' : '#d32f2f',
                            '&:hover': {
                                bgcolor: darkMode ? '#d32f2f' : '#b71c1c'
                            }
                        }}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    >
                        {loading ? 'Загрузка...' : 'Обновить'}
                    </Button>
                </DialogActions>
            </Dialog>
    )
}